import { useCallback } from "react";

import type { TopicItemSchema } from "app/pages/activation/components/Topics/Modal/types";
import type { SuggestionSchema, TopicSorting } from "app/pages/activation/components/Topics/types";
import { activationTracking } from "app/pages/activation/services/tracking";

import type { BenchmarkGranularity } from "../../../types/common";

import { useCreateFlows } from "./useCreateFlows";
import { useHandleSuccessCreation } from "./useHandleSuccessCreation";
import { useSuggestionItems } from "./useSuggestionItems";
import { useTopicSelection } from "./useTopicsSelection";

type UseAddTopicsSchema = {
    activationId: string;
    createdTopicsLength: number;
    createdTopics: SuggestionSchema[];
    granularity: BenchmarkGranularity;
    onClose: () => void;
    onSort: (item: { id: TopicSorting }) => void;
};

// Facade hook, try not to use it without real need and use granular hooks which used here.
export const useAddTopics = ({
    activationId,
    createdTopics,
    createdTopicsLength,
    granularity,
    onSort,
    onClose,
}: UseAddTopicsSchema) => {
    const track = activationTracking.home;

    const { itemsList, setItemsList, isSuggestionLoading } = useSuggestionItems({ activationId, createdTopics });

    const { selectedTopics, selectTopic, message, isMaxSelected, isMaxForOneAddSelected } = useTopicSelection({
        createdTopicsLength,
        track,
    });

    const handleSuccessCreation = useHandleSuccessCreation({ onClose, onSort });

    const { isLoading, onCreateFromKW, onCreateWithKeywords, onCreate, onCancel } = useCreateFlows({
        activationId,
        granularity,
        track,
        handleSuccessCreation,
    });

    const onCreateWithTopics = async () => {
        await onCreate(selectedTopics);
    };

    const onKeywordClick = useCallback(
        (keyword: string) => {
            if (!keyword) {
                return;
            }
            selectTopic(keyword, false)();
            setItemsList((prev) => [{ topicName: keyword } as Partial<TopicItemSchema>, ...prev]);
        },
        [selectTopic, setItemsList],
    );

    return {
        selectedTopics,
        itemsList,
        isMaxSelected,
        tooltipText: message,
        isLoading,
        isSuggestionLoading,
        isMaxForOneAddSelected,
        selectTopic,
        onKeywordClick,
        onCreate: onCreateWithTopics,
        onCancel,
        onCreateWithKeywords,
        onCreateFromKW,
    };
};
